import { useLanguage } from "@/context/LanguageContext";
import { translations } from "@/i18n/translations";
import { trackEvent } from "@/components/site/AnalyticsLoader";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_0455e07c-95ba-4da1-86cd-b6b640dcd3e8/artifacts/c1n4bm9e_Haceinda%20Carey%20Cover.png";

const HERO_BG =
  "https://images.unsplash.com/photo-1696418800218-6f3bb0975d3b?crop=entropy&cs=srgb&fm=jpg&q=55&w=900";

export default function LanguageGate() {
  const { setLang } = useLanguage();
  const en = translations.en.gate;
  const es = translations.es.gate;

  return (
    <div
      data-testid="language-gate"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A] text-white"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden="true"
      />
      {/* Dark overlay for contrast */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/85"
        aria-hidden="true"
      />
      {/* Warm accent glow */}
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-[#FF5533]/25 blur-[160px]"
        aria-hidden="true"
      />

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-6 py-16">
        <div className="flex max-w-3xl flex-col items-center text-center">
          {/* Logo */}
          <img
            src={LOGO_URL}
            alt="Hacienda Carey"
            className="mb-8 h-28 w-28 rounded-full object-cover ring-1 ring-white/15 shadow-2xl shadow-black/50 md:h-32 md:w-32"
            data-testid="language-gate-logo"
          />

          <p className="mb-4 text-xs uppercase tracking-[0.32em] text-[#FFB800]">
            {en.eyebrow}
          </p>

          <h1
            className="font-serif text-5xl leading-[1.05] tracking-tight md:text-7xl"
            style={{ fontFamily: "Fraunces, serif" }}
            data-testid="language-gate-headline"
          >
            <span className="block">{en.welcome}</span>
            <span className="mt-2 block text-white/55 italic">
              {es.welcome}
            </span>
          </h1>

          <p className="mt-6 max-w-md text-base text-white/70 md:text-lg">
            {en.sub} · {es.sub}
          </p>

          {/* CTA buttons */}
          <div className="mt-10 flex w-full flex-col gap-3 sm:flex-row sm:gap-4">
            <button
              type="button"
              onClick={() => {
                trackEvent("language_select", { language: "en", source: "gate" });
                setLang("en");
              }}
              data-testid="language-gate-en-button"
              className="group inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#FF5533] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#FF5533]/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#E64A2B] hover:shadow-xl hover:shadow-[#FF5533]/40 focus:outline-none focus:ring-2 focus:ring-[#FF5533]/50 focus:ring-offset-2 focus:ring-offset-black"
            >
              <span>{en.english}</span>
              <span
                aria-hidden="true"
                className="transition-transform duration-300 group-hover:translate-x-1"
              >
                →
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                trackEvent("language_select", { language: "es", source: "gate" });
                setLang("es");
              }}
              data-testid="language-gate-es-button"
              className="group inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-white/50 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              <span>{es.spanish}</span>
              <span
                aria-hidden="true"
                className="transition-transform duration-300 group-hover:translate-x-1"
              >
                →
              </span>
            </button>
          </div>

          <p className="mt-8 text-xs uppercase tracking-[0.24em] text-white/45">
            {en.hint}
          </p>
        </div>
      </div>

      {/* Bottom location strip */}
      <div className="absolute inset-x-0 bottom-6 z-10 flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/40">
        <span className="h-px w-8 bg-white/30" />
        <span>Aguadilla · Isabela · Puerto Rico</span>
        <span className="h-px w-8 bg-white/30" />
      </div>
    </div>
  );
}
